import { gql } from '@apollo/client';
import { IconButton, Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { TruncatedCell } from '~components/organization/AccessRequests/AccessRequestsSection';
import { MoreIconVertical } from '~components/ui/icons';
import { HandleApiError } from '~graphql/HandleApiError';
import {
  ServiceAdminSearchOrganizationsQuery,
  useServiceAdminSearchOrganizationsQuery,
} from './__generated__/OrganizationSearchResults.graphql';
import { AccessStateCell } from './AccessStateCell';
import { AddUserModal } from './AddUserModal';
import { DeveloperAccessSwitch } from './DeveloperAccessSwitch';
import { RequestAccessModal } from './RequestAccessModal';
import { SearchResultColumn, SearchResultsTable, useSearchPagination } from './SearchResultsTable';

type OrganizationResult =
  ServiceAdminSearchOrganizationsQuery['serviceAdminSearchOrganizations']['results'][number];

export function OrganizationSearchResults({ searchTerm }: { searchTerm: string }) {
  const { t } = useTranslation();
  const { verifyUserPermissions } = useAuth();
  const pagination = useSearchPagination();
  const [requestingAccessTo, setRequestingAccessTo] = useState<OrganizationResult | null>(null);
  const [addingUserTo, setAddingUserTo] = useState<OrganizationResult | null>(null);

  const canManageUsers = verifyUserPermissions([Permission.ServiceAdminUserManage]);

  const { data, loading, error, refetch } = useServiceAdminSearchOrganizationsQuery({
    variables: { searchTerm, pageSize: pagination.pageSize, pageNumber: pagination.pageIndex },
    fetchPolicy: 'cache-and-network',
  });

  const columns = useMemo<Array<SearchResultColumn<OrganizationResult>>>(
    () => [
      {
        id: 'name',
        header: t('name'),
        render: (organization) => (
          <TruncatedCell text={organization.name} maxWidth="48" fontWeight="semibold" />
        ),
      },
      {
        id: 'handle',
        header: t('handle'),
        render: (organization) => <TruncatedCell text={organization.handle} maxWidth="32" />,
      },
      {
        id: 'displayCount',
        header: t('displayCount'),
        render: (organization) => organization.displayCount,
      },
      {
        id: 'onboardedAdmin',
        header: t('onboardedAdmin'),
        render: (organization) => (
          <TruncatedCell text={organization.onboardedAdminEmail ?? '-'} maxWidth="40" />
        ),
      },
      {
        id: 'createdAt',
        header: t('dateCreated'),
        render: (organization) => format(new Date(organization.createdAt), 'MMM d, yyyy'),
      },
      {
        id: 'developerAccess',
        header: t('prostoreDevAccess'),
        render: (organization) => <DeveloperAccessSwitch organization={organization} />,
      },
      {
        id: 'accessState',
        header: '',
        render: (organization) => (
          <AccessStateCell
            organizationId={organization.id}
            accessState={organization.accessState}
            onRequestAccess={() => setRequestingAccessTo(organization)}
          />
        ),
      },
      {
        id: 'actions',
        header: '',
        width: '16',
        render: (organization) =>
          canManageUsers && (
            <Menu placement="bottom-end">
              <MenuButton
                as={IconButton}
                variant="unstyled"
                display="flex"
                alignItems="center"
                justifyContent="center"
                minWidth="8"
                height="8"
                aria-label={t('actions')}
                icon={<MoreIconVertical color="blue.400" width="5" height="5" />}
              />
              <MenuList>
                <MenuItem onClick={() => setAddingUserTo(organization)}>{t('addUser')}</MenuItem>
              </MenuList>
            </Menu>
          ),
      },
    ],
    [t, canManageUsers],
  );

  if (error) {
    return <HandleApiError error={error} />;
  }

  return (
    <>
      <SearchResultsTable
        isLoading={loading && !data}
        rows={data?.serviceAdminSearchOrganizations.results ?? []}
        totalCount={data?.serviceAdminSearchOrganizations.totalCount ?? 0}
        columns={columns}
        pagination={pagination}
      />
      <RequestAccessModal
        organization={requestingAccessTo}
        onClose={() => setRequestingAccessTo(null)}
        onRequested={refetch}
      />
      <AddUserModal organization={addingUserTo} onClose={() => setAddingUserTo(null)} />
    </>
  );
}

OrganizationSearchResults.graphql = {
  queries: {
    ServiceAdminSearchOrganizations: gql`
      query ServiceAdminSearchOrganizations(
        $searchTerm: String!
        $pageSize: Int
        $pageNumber: Int
      ) {
        serviceAdminSearchOrganizations(
          searchTerm: $searchTerm
          pageSize: $pageSize
          pageNumber: $pageNumber
        ) {
          totalCount
          results {
            id
            name
            handle
            displayCount
            onboardedAdminEmail
            createdAt
            proStoreConnected
            developerAccess
            accessState
          }
        }
      }
    `,
  },
};
